import type { FavoriteView, InstrumentRef } from "@screener/protocol";

/**
 * Сопоставление инструмента и монеты строки с избранным §6.6 — на стороне
 * клиента: признака избранного ни в снимке, ни в потоке §7.6 нет
 * (спека `user-favorites`, design.md change stage6-favorites, решение 5).
 *
 * Две функции, а не одна, — прямое следствие спеки `web-density-table`:
 * фильтр «только избранные» относит инструмент к записи, у которой биржа и
 * рынок либо пусты, либо совпадают (любая гранулярность записи), а звёздочка
 * строки зажигается только записью «монета целиком» — иначе нажатие на
 * горящую звёздочку добавляло бы новую запись вместо снятия уже стоящей.
 */

/**
 * Состояние списка избранного §6.6 — тот же снаряд, что у закреплений и
 * заметок. Живёт здесь, а не в `use-favorites.ts`: это чистый тип без React,
 * которым пользуются `derive.ts` и `situation-derive.ts` (отбор «только
 * избранные») без зависимости от хука (review stage6-favorites, п. 6).
 * `use-favorites.ts` реэкспортирует его для потребителей самого хука.
 */
export type FavoritesState =
  | { status: "loading" }
  | { status: "ready"; favorites: FavoriteView[] }
  | { status: "unavailable" };

/**
 * Инструмент относится к записи избранного: базовая монета совпадает, а
 * биржа и рынок записи либо пусты («любая»), либо равны бирже и рынку
 * инструмента. Котировка в записи нет — она в сравнение не входит (§8/§9
 * говорят об избранных монетах, а не парах).
 */
export function isFavoriteInstrument(
  instrument: InstrumentRef,
  favorites: readonly FavoriteView[],
): boolean {
  return favorites.some((favorite) => {
    if (favorite.base !== instrument.base) {
      return false;
    }
    if (favorite.exchange !== null && favorite.exchange !== instrument.exchange) {
      return false;
    }
    if (favorite.market !== null && favorite.market !== instrument.market) {
      return false;
    }
    return true;
  });
}

/**
 * Монета отмечена избранной целиком — запись с пустыми биржей и рынком.
 * Запись на отдельной бирже или рынке §6.6 допускает, но звёздочку не
 * зажигает: она показывает ровно ту запись, которую её действие ставит
 * и снимает (web-density-table).
 */
export function isWholeCoinFavorite(base: string, favorites: readonly FavoriteView[]): boolean {
  return favorites.some(
    (favorite) => favorite.base === base && favorite.exchange === null && favorite.market === null,
  );
}
